import React, { useEffect, useRef, useState } from 'react';

interface GlitchImageProps {
    src: string;
    alt?: string;
    width?: number | string;
    height?: number | string;
    isGlitch?: boolean;
}

class GlitchProcessor {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private img: HTMLImageElement | null = null;
    private copyData: Uint8ClampedArray | null = null;
    private channelLen = 4;
    private animationFrameId: number | null = null;
    private timeoutId: NodeJS.Timeout | null = null;
    private glitchActive = false;
    private glitchInterval = 75; // 100ms 마다 효과 적용

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d')!;
    }

    loadImage(img: HTMLImageElement) {
        this.canvas.width = img.width;
        this.canvas.height = img.height;
        this.img = img;
        this.ctx.drawImage(img, 0, 0);
        const imageData = this.ctx.getImageData(0, 0, img.width, img.height);
        this.copyData = new Uint8ClampedArray(imageData.data);
    }

    flowLine(obj: { t1: number; speed: number; randX: number }) {
        if (!this.copyData || !this.img) return;
        
        const destPixels = new Uint8ClampedArray(this.copyData);
        const { width, height } = this.img;
        
        obj.t1 %= height;
        obj.t1 += obj.speed;
        const tempY = Math.floor(obj.t1);

        const rowStart = tempY * width * this.channelLen;
        const rowEnd = rowStart + width * this.channelLen;
        
        for (let i = rowStart; i < rowEnd; i += this.channelLen) {
            destPixels[i] = Math.min(255, this.copyData[i] + obj.randX);
            destPixels[i + 1] = Math.min(255, this.copyData[i + 1] + obj.randX);
            destPixels[i + 2] = Math.min(255, this.copyData[i + 2] + obj.randX);
        }

        this.replaceData(destPixels);
    }

    shiftLine() {
        if (!this.copyData || !this.img) return;

        const destPixels = new Uint8ClampedArray(this.copyData);
        const { width, height } = this.img;

        const rangeMin = Math.floor(Math.random() * height * 0.6);
        const rangeMax = Math.min(rangeMin + Math.floor(Math.random() * 60), height);
        const offsetX = Math.floor((Math.random() - 0.5) * 60) * this.channelLen;

        for (let y = rangeMin; y < rangeMax; y++) {
            const rowStart = y * width * this.channelLen;
            for (let x = 0; x < width; x++) {
                const index = rowStart + x * this.channelLen;
                const r2 = Math.max(0, Math.min(index + offsetX, destPixels.length - 1));
                const g2 = Math.max(0, Math.min(index + 1 + offsetX, destPixels.length - 1));
                const b2 = Math.max(0, Math.min(index + 2 + offsetX, destPixels.length - 1));

                destPixels[index] = this.copyData[r2];
                destPixels[index + 1] = this.copyData[g2];
                destPixels[index + 2] = this.copyData[b2];
            }
        }

        this.replaceData(destPixels);
    }

    shiftRGB() {
        if (!this.copyData) return;

        const destPixels = new Uint8ClampedArray(this.copyData);
        const len = destPixels.length;
        const range = 20;

        const randR = Math.floor((Math.random() - 0.5) * range * 2) * this.channelLen;
        const randG = Math.floor((Math.random() - 0.5) * range * 2) * this.channelLen;
        const randB = Math.floor((Math.random() - 0.5) * range * 2) * this.channelLen;

        for (let i = 0; i < len; i += this.channelLen) {
            const r2 = (i + randR + len) % len;
            const g2 = (i + randG + len) % len;
            const b2 = (i + randB + len) % len;

            destPixels[i] = this.copyData[r2];
            destPixels[i + 1] = this.copyData[g2];
            destPixels[i + 2] = this.copyData[b2];
        }

        this.replaceData(destPixels);
    }

    scatImage() {
        if (!this.copyData || !this.img) return;

        const { width, height } = this.img;
        const destPixels = new Uint8ClampedArray(this.copyData);

        const startX = Math.floor(Math.random() * (width - 20));
        const startY = Math.floor(Math.random() * (height - 20));
        const rectW = Math.floor(Math.random() * 40 + 15);
        const rectH = Math.floor(Math.random() * 20 + 3);

        for (let y = startY; y < Math.min(startY + rectH, height); y++) {
            for (let x = startX; x < Math.min(startX + rectW, width); x++) {
                const srcIdx = (y * width + x) * this.channelLen;
                const dstX = Math.min(width - 1, Math.max(0, x + Math.floor((Math.random() - 0.5) * 30)));
                const dstY = Math.min(height - 1, Math.max(0, y + Math.floor((Math.random() - 0.5) * 15)));
                const dstIdx = (dstY * width + dstX) * this.channelLen;

                destPixels[dstIdx] = this.copyData[srcIdx];
                destPixels[dstIdx + 1] = this.copyData[srcIdx + 1];
                destPixels[dstIdx + 2] = this.copyData[srcIdx + 2];
            }
        }

        this.replaceData(destPixels);
    }

    private replaceData(pixels: Uint8ClampedArray) {
        if (!this.img) return;
        const imageData = this.ctx.createImageData(this.img.width, this.img.height);
        imageData.data.set(pixels);
        this.ctx.putImageData(imageData, 0, 0);
    }

    applyGlitch() {
        if (!this.copyData || !this.img) return;

        // 원본으로 복구
        this.ctx.drawImage(this.img, 0, 0);

        const effects = [
            () => this.flowLine({ t1: Math.random() * 50, speed: Math.floor(Math.random() * 15 + 3), randX: Math.floor(Math.random() * 60 + 15) }),
            () => this.shiftLine(),
            () => this.shiftRGB(),
            () => this.scatImage(),
        ];

        // 1-2개 랜덤 효과 적용
        const numEffects = Math.random() > 0.5 ? 1 : 2;
        for (let i = 0; i < numEffects; i++) {
            const effect = effects[Math.floor(Math.random() * effects.length)];
            effect();
        }
    }

    startGlitch() {
        this.glitchActive = true;
        const applyFrame = () => {
            if (this.glitchActive) {
                this.applyGlitch();
                this.timeoutId = setTimeout(applyFrame, this.glitchInterval);
            }
        };
        this.timeoutId = setTimeout(applyFrame, this.glitchInterval);
    }

    stopGlitch() {
        this.glitchActive = false;
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
        }
        // 원본으로 복구
        if (this.img) {
            this.ctx.drawImage(this.img, 0, 0);
        }
    }
}

export const GlitchImage: React.FC<GlitchImageProps> = ({ 
    src, 
    alt = '', 
    width = 322, 
    height = 80,
    isGlitch = false 
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const processorRef = useRef<GlitchProcessor | null>(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        processorRef.current = new GlitchProcessor(canvasRef.current);

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            processorRef.current?.loadImage(img);
            // 초기 상태 표시
            canvasRef.current!.getContext('2d')?.drawImage(img, 0, 0);
        };
        img.src = src;
    }, [src]);

    useEffect(() => {
        if (isGlitch && processorRef.current) {
            processorRef.current.startGlitch();
        } else if (!isGlitch && processorRef.current) {
            processorRef.current.stopGlitch();
        }
    }, [isGlitch]);

    return (
        <canvas
            ref={canvasRef}
            width={width}
            height={height}
            style={{
                display: 'block',
                width: '100%',
                height: '100%',
            }}
        />
    );
};

export default GlitchImage;
